import axios from 'axios';
import * as z from 'zod';

import { resources } from '../locales/resources';
import { SEO } from '../services/strapiSchema';
import { seo } from '../services/strapiBlocks';

const PRIMARY_LOCALE = 'fr-FR';
const PAGE_SIZE = 100;

/** Nombre d'actualités par page de la liste. Partagé avec `newsService`. */
export const NEWS_PER_PAGE = 9;

type Locale = keyof typeof resources;

export type CmsRouteEntry = {
  slug: string;
  breadcrumb: string;
};

export type CmsNewsPageEntry = CmsRouteEntry & {
  /** Nombre de pages de la liste d'actualités (au moins 1). */
  pageCount: number;
};

export type CmsLocaleRoutes = {
  pages: CmsRouteEntry[];
  newsPage: CmsNewsPageEntry | null;
  offresEtServices: CmsRouteEntry | null;
};

export type CmsRoutesData = Record<Locale, CmsLocaleRoutes>;

const SlugEntryZod = z.object({
  slug: z.string(),
  seo: SEO.pick({ metaTitle: true, breadcrumbTitle: true }),
});

const isNotFoundError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

const logMissing = (resource: string, locale: string) => {
  // eslint-disable-next-line no-console
  console.warn(`[cms-routes] "${resource}" not found for locale "${locale}", skipping.`);
};

const toRouteEntry = (raw: unknown): CmsRouteEntry => {
  const { slug, seo: { breadcrumbTitle, metaTitle } } = SlugEntryZod.parse(raw);
  return { slug, breadcrumb: breadcrumbTitle || metaTitle };
};

const fetchPages = async (cmsUrl: string, locale: Locale): Promise<CmsRouteEntry[]> => {
  const fetchPage = (page: number) => axios.get(`${cmsUrl}/api/pages`, {
    params: {
      locale,
      fields: ['slug'],
      populate: { seo },
      pagination: { page, pageSize: PAGE_SIZE },
    },
  });

  try {
    const first = await fetchPage(1);
    const pageCount = first.data?.meta?.pagination?.pageCount ?? 1;
    const others = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, i) => fetchPage(i + 2)),
    );

    return [first, ...others]
      .flatMap((response) => response.data?.data ?? [])
      .map(toRouteEntry);
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissing('pages', locale);
      return [];
    }
    throw error;
  }
};

const fetchSingleType = async (
  cmsUrl: string,
  endpoint: 'news-page' | 'offres-et-services',
  locale: Locale,
): Promise<CmsRouteEntry | null> => {
  try {
    const response = await axios.get(`${cmsUrl}/api/${endpoint}`, {
      params: { locale, fields: ['slug'], populate: { seo } },
    });

    if (!response.data?.data) {
      if (locale !== PRIMARY_LOCALE) {
        logMissing(endpoint, locale);
        return null;
      }
      throw new Error(`No ${endpoint} found`);
    }

    return toRouteEntry(response.data.data);
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissing(endpoint, locale);
      return null;
    }
    throw error;
  }
};

const fetchNewsPageCount = async (cmsUrl: string, locale: Locale): Promise<number> => {
  try {
    const response = await axios.get(`${cmsUrl}/api/news`, {
      params: {
        locale,
        fields: ['documentId'],
        pagination: { page: 1, pageSize: NEWS_PER_PAGE },
      },
    });
    return Math.max(1, response.data?.meta?.pagination?.pageCount ?? 1);
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissing('news', locale);
      return 1;
    }
    throw error;
  }
};

export async function fetchCmsRoutesData(cmsUrl: string): Promise<CmsRoutesData> {
  const locales = Object.keys(resources) as Locale[];

  const entries = await Promise.all(
    locales.map(async (locale) => {
      const [pages, newsPageEntry, newsPageCount, offresEtServices] = await Promise.all([
        fetchPages(cmsUrl, locale),
        fetchSingleType(cmsUrl, 'news-page', locale),
        fetchNewsPageCount(cmsUrl, locale),
        fetchSingleType(cmsUrl, 'offres-et-services', locale),
      ]);
      const newsPage = newsPageEntry ? { ...newsPageEntry, pageCount: newsPageCount } : null;

      return [locale, { pages, newsPage, offresEtServices }] as const;
    }),
  );

  return Object.fromEntries(entries) as CmsRoutesData;
}
